import { useEffect, useCallback } from 'react'
import { drawerMessageBus, type DrawerMessage } from '@/components/ui/drawer/DrawerProvider'

export function useDrawerMessaging(drawerId: string) {
  const sendMessage = useCallback((message: Omit<DrawerMessage, 'from'>) => {
    const event = new CustomEvent('drawer-message', {
      detail: { ...message, from: drawerId }
    })
    drawerMessageBus.dispatchEvent(event)
  }, [drawerId])
  
  const onMessage = useCallback((
    handler: (message: DrawerMessage) => void,
    filter?: { from?: string; action?: DrawerMessage['action'] }
  ) => {
    const listener = (e: Event) => {
      const customEvent = e as CustomEvent<DrawerMessage>
      const message = customEvent.detail
      
      // Check if message is for this drawer
      if (message.to !== drawerId && message.to !== '*') {
        return
      }
      
      // Apply filters if provided
      if (filter) {
        if (filter.from && message.from !== filter.from) {
          return
        }
        if (filter.action && message.action !== filter.action) {
          return
        }
      }
      
      handler(message)
    }
    
    drawerMessageBus.addEventListener('drawer-message', listener)
    return () => drawerMessageBus.removeEventListener('drawer-message', listener)
  }, [drawerId])

  // Broadcast to all drawers
  const broadcast = useCallback((
    action: DrawerMessage['action'],
    data?: unknown
  ) => {
    sendMessage({
      to: '*',
      action,
      data
    })
  }, [sendMessage])

  // Send to specific drawer
  const sendTo = useCallback((
    targetId: string,
    action: DrawerMessage['action'],
    data?: unknown
  ) => {
    sendMessage({
      to: targetId,
      action,
      data
    })
  }, [sendMessage])

  return { 
    sendMessage, 
    onMessage, 
    broadcast,
    sendTo
  }
}

// Example usage helper hooks
export function useDrawerRefresh(drawerId: string, onRefresh: () => void) {
  const { onMessage } = useDrawerMessaging(drawerId)
  
  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        onRefresh()
      }
    })
  }, [onMessage, onRefresh])
}

export function useDrawerValidation(drawerId: string, onValidate: () => boolean) {
  const { onMessage, sendTo } = useDrawerMessaging(drawerId)
  
  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'validate') {
        const isValid = onValidate()
        if (message.from) {
          sendTo(message.from, 'validate', { isValid })
        }
      }
    })
  }, [onMessage, sendTo, onValidate])
}